import { useCallback, useMemo, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { ErrorModal } from "@dexteel/mesf-core";
import {
	Group,
	Panel,
	Separator,
	useDefaultLayout,
} from "react-resizable-panels";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { DeleteConfirmModal } from "@/controls/modals/DeleteConfirmModal";
import type { RollApplication } from "../models/RollApplication";
import { ROLL_APPLICATION_INITIAL_VALUES } from "../models/RollApplication";
import {
	useRollApplications,
	useRollMaterials,
} from "../hooks/useRollApplications";
import {
	useDeleteRollApplication,
	useUpsertRollApplication,
} from "../hooks/useRollApplicationMutation";
import { RollApplicationsTable } from "./RollApplicationsTable";
import { RollApplicationDetailViewer } from "./RollApplicationDetailViewer";
import { RollApplicationsToolbar } from "./RollApplicationsToolbar";
import { SHOW_ONLY_ACTIVES_DEFAULT } from "@/controls/filters/ShowOnlyActivesCheckbox";
import {
	urlParam,
	useDropUnknownValue,
	useUrlState,
	useUrlText,
} from "@/shared/hooks/useUrlState";
import { useUrlSelection } from "@/shared/hooks/useUrlSelection";

const DEFAULT_LAYOUT = { left: 60, right: 40 };

const URL_SCHEMA = {
	search: urlParam.string(),
	onlyActives: urlParam.bool(SHOW_ONLY_ACTIVES_DEFAULT),
	rollMaterialId: urlParam.int(),
	rollApplicationId: urlParam.int(),
};

export const RollApplicationsManager = () => {
	// Filters and the selected record, all in the query string.
	const [
		{ search, onlyActives, rollMaterialId, rollApplicationId },
		setFilters,
		{ dropValues, resetValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	// The box types locally and the URL follows once typing stops.
	const [searchText, setSearchText] = useUrlText(search ?? "", (value) =>
		setFilters({ search: value || null }),
	);
	// A link to a roll material that is no longer offered falls back to all of them.
	const {
		data: rollMaterialOptions = [],
		isSuccess: rollMaterialOptionsLoaded,
	} = useRollMaterials();
	useDropUnknownValue(
		rollMaterialId,
		rollMaterialOptionsLoaded,
		rollMaterialOptions.map((option) => option.Id),
		useCallback(() => dropValues("rollMaterialId"), [dropValues]),
	);
	const [error, setError] = useState("");
	const [notification, setNotification] = useState("");
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [appToDelete, setAppToDelete] = useState<RollApplication | null>(null);

	const { defaultLayout, onLayoutChange } = useDefaultLayout({
		id: "roll-applications-manager-v1",
		panelIds: ["left", "right"],
		storage: localStorage,
	});

	const {
		rollApplications,
		isLoading,
		error: queryError,
	} = useRollApplications();

	const filteredApplications = useMemo(() => {
		let result = onlyActives
			? rollApplications.filter((a) => a.Active)
			: rollApplications;
		if (searchText) {
			const s = searchText.toLowerCase();
			result = result.filter((a) => a.Name?.toLowerCase().includes(s));
		}
		if (rollMaterialId) {
			result = result.filter((a) => a.RollMaterialId === rollMaterialId);
		}
		return result;
	}, [rollApplications, searchText, rollMaterialId, onlyActives]);

	// The id travels and the record is resolved from the list. A record being added has
	// no id yet, so it stays out of the URL.
	const selection = useUrlSelection<RollApplication>({
		id: rollApplicationId,
		setId: (value) => setFilters({ rollApplicationId: value }),
		dropId: () => dropValues("rollApplicationId"),
		rows: rollApplications,
		ready: !isLoading,
		getId: (record) => record.Id,
	});
	const selectedApplication = selection.current;

	const { upsertRollApplication, isUpserting } = useUpsertRollApplication({
		onSuccess: (data) => {
			const isNew = selection.isNew;
			setNotification(
				isNew
					? "Roll application created successfully"
					: "Roll application updated successfully",
			);
			if (data) {
				selection.adopt(data);
			}
		},
		onError: (err) => setError(err.message),
	});

	const { deleteRollApplication, isDeleting } = useDeleteRollApplication({
		onSuccess: () => {
			setNotification("Roll application deleted successfully");
			selection.clear();
			setDeleteModalOpen(false);
			setAppToDelete(null);
		},
		onError: (err) => setError(err.message),
	});

	const handleRowSelected = useCallback(
		(app: RollApplication | null) => {
			if (app) selection.select(app);
			else selection.clear();
		},
		[selection],
	);

	const handleDelete = useCallback((app: RollApplication) => {
		setAppToDelete(app);
		setDeleteModalOpen(true);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (appToDelete?.Id) {
			deleteRollApplication(appToDelete.Id);
		}
	}, [appToDelete, deleteRollApplication]);

	const handleCancelDelete = useCallback(() => {
		setDeleteModalOpen(false);
		setAppToDelete(null);
	}, []);

	const handleAdd = useCallback(() => {
		selection.startNew({ ...ROLL_APPLICATION_INITIAL_VALUES });
	}, [selection]);

	const handleSave = useCallback(
		(app: RollApplication) => {
			upsertRollApplication(app);
		},
		[upsertRollApplication],
	);

	const handleClose = useCallback(() => {
		selection.clear();
	}, [selection]);

	if (queryError) {
		return <ErrorModal error={queryError.message} onHide={() => {}} />;
	}

	return (
		<Box
			sx={{
				width: "100%",
				height: "calc(100vh - 130px)",
				display: "flex",
				flexDirection: "column",
				gap: 1,
				p: 1,
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Roll Applications
			</Typography>

			<Paper elevation={3}>
				<RollApplicationsToolbar
					onReset={() =>
						resetValues(["search", "onlyActives", "rollMaterialId"])
					}
					canReset={!isDefault("search", "onlyActives", "rollMaterialId")}
					search={searchText}
					onSearchChange={setSearchText}
					rollMaterialId={rollMaterialId}
					onRollMaterialChange={(value) =>
						setFilters({ rollMaterialId: value })
					}
					onlyActives={onlyActives}
					onOnlyActivesChange={(value) => setFilters({ onlyActives: value })}
					onAdd={handleAdd}
				/>
			</Paper>

			<Group
				id="roll-applications-manager-group"
				orientation="horizontal"
				defaultLayout={defaultLayout ?? DEFAULT_LAYOUT}
				onLayoutChange={onLayoutChange}
				style={{ flex: 1, width: "100%" }}
			>
				<Panel id="left" minSize={25} style={{ paddingBottom: 5 }}>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							height: "100%",
							pr: 1,
						}}
					>
						<Paper
							elevation={3}
							sx={{ flex: 1, width: "100%", overflow: "hidden" }}
						>
							{isLoading ? (
								<CenteredLazyLoading />
							) : (
								<RollApplicationsTable
									rollApplications={filteredApplications}
									selectedApplication={
										selectedApplication?.Id ? selectedApplication : null
									}
									onRowSelected={handleRowSelected}
									onDelete={handleDelete}
								/>
							)}
						</Paper>
					</Box>
				</Panel>

				<Separator
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "col-resize",
					}}
				>
					<Box
						sx={{
							width: 6,
							height: 40,
							borderRadius: 3,
							backgroundColor: "divider",
							"&:hover": { backgroundColor: "primary.main" },
						}}
					/>
				</Separator>

				<Panel id="right" minSize={30} style={{ paddingBottom: 5 }}>
					<Box sx={{ pl: 1, height: "100%" }}>
						<Paper
							elevation={3}
							sx={{
								height: "100%",
								overflowY: "auto",
								overflowX: "hidden",
							}}
						>
							<RollApplicationDetailViewer
								application={selectedApplication}
								existingApplications={rollApplications}
								isSaving={isUpserting || isDeleting}
								onSave={handleSave}
								onClose={handleClose}
							/>
						</Paper>
					</Box>
				</Panel>
			</Group>

			<DeleteConfirmModal
				open={deleteModalOpen}
				title="DELETE ROLL APPLICATION"
				itemName={appToDelete?.Name ?? ""}
				isDeleting={isDeleting}
				onConfirm={handleConfirmDelete}
				onCancel={handleCancelDelete}
			/>

			<NotificationSnackBar
				message={notification}
				onHide={() => setNotification("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Box>
	);
};
